import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CardMedia } from '@mui/material';

/**
 * SmartImage - Componente de imagen inteligente con fallback entre buckets
 * 
 * Si la imagen falla en cargar desde un bucket (staging o production),
 * automáticamente intenta cargar desde el bucket alternativo.
 * 
 * Buckets soportados:
 * - staging-exports-web (staging/QA)
 * - production-website-images (production)
 */

const STAGING_BUCKET = 'staging-exports-web';
const PRODUCTION_BUCKET = 'production-website-images';
const DEFAULT_FALLBACK = '/assets/image-placeholder.webp';

/**
 * Intercambia el bucket en una URL de S3
 * @param {string} url - URL original
 * @returns {string} URL con bucket alternativo
 */
const swapBucket = (url) => {
  if (!url || typeof url !== 'string') return url;
  
  if (url.includes(STAGING_BUCKET)) {
    return url.replace(STAGING_BUCKET, PRODUCTION_BUCKET);
  }
  if (url.includes(PRODUCTION_BUCKET)) {
    return url.replace(PRODUCTION_BUCKET, STAGING_BUCKET);
  }
  
  return url;
};

/**
 * Componente SmartImage - img estándar con fallback inteligente
 */
const SmartImage = ({
  src,
  alt = '',
  fallback = DEFAULT_FALLBACK,
  className = '',
  style = {},
  onLoad,
  onError,
  ...rest
}) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [triedAlternative, setTriedAlternative] = useState(false);

  // Reset cuando cambia la URL original
  useEffect(() => {
    setCurrentSrc(src);
    setTriedAlternative(false);
  }, [src]);

  const handleError = useCallback(() => {
    if (!triedAlternative && currentSrc) {
      const alternativeSrc = swapBucket(currentSrc);
      
      if (alternativeSrc !== currentSrc) {
        setCurrentSrc(alternativeSrc);
        setTriedAlternative(true);
        return;
      }
    }
    
    if (currentSrc !== fallback) {
      setCurrentSrc(fallback);
    }
    
    if (onError) {
      onError();
    }
  }, [currentSrc, triedAlternative, fallback, onError]);

  const handleLoad = useCallback(() => {
    if (onLoad) {
      onLoad();
    }
  }, [onLoad]);

  return (
    <img
      src={currentSrc || fallback}
      alt={alt}
      className={className}
      style={style}
      onError={handleError}
      onLoad={handleLoad}
      {...rest}
    />
  );
};

SmartImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  fallback: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  onLoad: PropTypes.func,
  onError: PropTypes.func
};

/**
 * SmartCardMedia - CardMedia de MUI con fallback inteligente entre buckets
 * Usa component="img" internamente para manejar onError
 */
const SmartCardMedia = ({
  image,
  alt = '',
  fallback = DEFAULT_FALLBACK,
  height = 200,
  sx = {},
  ...rest
}) => {
  const [currentImage, setCurrentImage] = useState(image);
  const [triedAlternative, setTriedAlternative] = useState(false);

  // Reset cuando cambia la imagen original
  useEffect(() => {
    setCurrentImage(image);
    setTriedAlternative(false);
  }, [image]);

  const handleError = useCallback(() => {
    if (!triedAlternative && currentImage) {
      const alternativeImage = swapBucket(currentImage);
      
      if (alternativeImage !== currentImage) {
        setCurrentImage(alternativeImage);
        setTriedAlternative(true);
        return;
      }
    }
    
    if (currentImage !== fallback) {
      setCurrentImage(fallback);
    }
  }, [currentImage, triedAlternative, fallback]);

  return (
    <CardMedia
      component="img"
      height={height}
      image={currentImage || fallback}
      alt={alt}
      onError={handleError}
      sx={{
        objectFit: 'cover',
        backgroundColor: 'grey.200',
        ...sx
      }}
      {...rest}
    />
  );
};

SmartCardMedia.propTypes = {
  image: PropTypes.string,
  alt: PropTypes.string,
  fallback: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object
};

export default SmartImage;
export { SmartCardMedia, swapBucket, STAGING_BUCKET, PRODUCTION_BUCKET, DEFAULT_FALLBACK };
